// Theme Engine - Central Export Hub
// This file exports everything needed for consistent theming across the entire app

// Import only the utilities that are actually used in the app
import {
  createPageContainer,
  createScrollableContent,
  createFullHeightLayout,
  createPageContent,
  createSidebar,
  createMainArea,
  createGlassmorphism,
  createHoverEffect,
  createShimmerEffect,
  createConstructionPattern,
  createDotPattern,
  createScrollbarStyles,
  createButtonStyles,
  createInputStyles,
  createTableStyles,
  createAnimationStyles,
  createResponsiveStyles,
  createThemeAwareStyles,
  getContrastColor,
  createStatusColors,
  createElevationStyles,
  createDialogStyles,
  createSnackbarStyles,
  createDialogLayout,
  createUpdateNotificationStyles
} from './utils';

// Import constants
import { COMMON_STYLES } from './constants';

// Core theme creation
export { createAppTheme } from './themeEngine';

// Theme constants and presets
export {
  ANIMATIONS,
  SHADOWS,
  TRANSITIONS,
  EASINGS,
  PAGE_LAYOUT,
  SPACING,
  BORDER_RADIUS,
  PRIMARY_PRESET_COLORS,
  SECONDARY_PRESET_COLORS,
  THEME_VARIANTS,
  DEFAULT_COLORS
} from './constants';

// Export only the utility functions that are actually used
export {
  createPageContainer,
  createScrollableContent,
  createFullHeightLayout,
  createPageContent,
  createSidebar,
  createMainArea,
  createGlassmorphism,
  createHoverEffect,
  createShimmerEffect,
  createConstructionPattern,
  createDotPattern,
  createScrollbarStyles,
  createButtonStyles,
  createInputStyles,
  createTableStyles,
  createAnimationStyles,
  createResponsiveStyles,
  createThemeAwareStyles,
  getContrastColor,
  createStatusColors,
  createElevationStyles,
  createDialogStyles,
  createSnackbarStyles,
  createDialogLayout,
  createUpdateNotificationStyles
} from './utils';

// Layout components
export {
  PageContainer,
  ScrollableContent,
  FullHeightLayout,
  MainContent,
  PageContent,
  SidebarLayout,
  Sidebar,
  MainArea,
  FixedHeader,
  FixedFooter,
  ResponsivePageWrapper,
  ContentWrapper,
  ScrollableArea,
  AppPageLayout,
  HeaderConstrainedContent,
  CenteredPageContent,
  GridPageLayout,
  FlexPageLayout,
  SplitPageLayout,
  DashboardLayout,
  ScrollableContentArea,
  SidebarScrollableLayout,
  SplitDialog,
  FeatureCard,
  UploadZone,
  DialogInfoSection,
  DialogTip,
  StandardDialog,
  FullWidthDialog,
  WizardDialog
} from './components/PageLayout';

// Theme-aware component styles
export const ThemeStyles = {
  // Common component styles that can be reused
  card: (theme, options = {}) => ({
    ...COMMON_STYLES.card,
    backgroundColor: theme.palette.background.paper,
    borderColor: theme.palette.divider,
    ...options
  }),
  
  button: (theme, variant = 'contained', color = 'primary') => 
    createButtonStyles(theme, variant, theme.palette[color]?.main || color),
  
  input: (theme, color = 'primary') => 
    createInputStyles(theme, theme.palette[color]?.main || color),
  
  scrollbar: (theme, color = 'primary', type = 'default') => 
    createScrollbarStyles(theme, theme.palette[color]?.main || color, type),
  
  dialog: (theme, options = {}) => 
    createDialogStyles(theme, options),
  
  snackbar: (theme, options = {}) => 
    createSnackbarStyles(theme, options),
  
  updateNotification: (theme, options = {}) => 
    createUpdateNotificationStyles(theme, options),
  
  table: (theme, color = 'primary') => 
    createTableStyles(theme, theme.palette[color]?.main || color),
  
  glassmorphism: (theme, opacity = 0.7) => 
    createGlassmorphism(theme, opacity),
  
  hover: (theme, color = 'primary', intensity = 0.08) => 
    createHoverEffect(theme, theme.palette[color]?.main || color, intensity),
  
  elevation: (level = 1) => 
    createElevationStyles(level)
};

// Pre-configured layout patterns
export const LayoutPatterns = {
  // Standard page with header and content
  standardPage: (theme) => ({
    container: createPageContainer(theme),
    content: createPageContent(theme, { responsive: true })
  }),
  
  // Dashboard layout with sidebar
  dashboard: (theme) => ({
    container: createFullHeightLayout(theme),
    sidebar: createSidebar(theme, { width: '280px' }),
    main: createMainArea(theme),
    content: createScrollableContent(theme)
  }),
  
  // Split layout for dialogs
  splitDialog: (theme, options = {}) => 
    createDialogLayout(theme, 'split', options),
  
  // Wizard dialog layout
  wizardDialog: (theme, options = {}) => 
    createDialogLayout(theme, 'wizard', options)
};

// Color utilities
export const ColorUtils = {
  getContrastColor: (theme, color) => getContrastColor(theme, color),
  createStatusColors: (theme) => createStatusColors(theme),
  alpha: (color, opacity) => `rgba(${color}, ${opacity})`
};

// Animation utilities
export const AnimationUtils = {
  shimmer: (theme) => createShimmerEffect(theme),
  construction: (theme, color) => createConstructionPattern(theme, color),
  dotPattern: (theme, color, size) => createDotPattern(theme, color, size),
  fadeIn: createAnimationStyles('fadeIn'),
  slideUp: createAnimationStyles('slideUp'),
  scaleIn: createAnimationStyles('scaleIn')
};

// Responsive utilities
export const ResponsiveUtils = {
  createResponsiveStyles,
  createThemeAwareStyles,
  breakpoints: {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1200,
    xl: 1536
  }
};

// Main theme exports
export { ThemeProvider } from './ThemeProvider';
export { useTheme } from './hooks/useTheme';

// Theme building blocks
export { createPalette } from './palettes';
export { createTypography } from './typography';
export { createComponents } from './components';

// Constants and presets
export * from './constants';
export * from './presets';

// Utilities and helpers
export * from './utils';

// Page layout components
export * from './components/PageLayout';

// Templates and examples
export { default as CardBasedPageTemplate } from './templates/CardBasedPage';

// Re-export Material-UI utilities for convenience
export { alpha, darken, lighten } from '@mui/material/styles'; 